import r2wc from '@r2wc/react-to-web-component';
import { useState, useEffect, useRef } from 'react';

import { windowIsDefined } from '@klv/shared/utils/functions';
import { Tooltip, TooltipTrigger, TooltipContent } from '@klv/atoms/tooltip';
import Icon from '@klv/atoms/icon';


import * as styles from './styles/klv-tooltip.module.scss';

import type { IKlvTooltipProps } from './types';

// 1. Create a wrapper component that renders our Tooltip with the desired props.
export const KlvTooltipWrapper = ({ title, body }: IKlvTooltipProps) => {
  const [open, setOpen] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  return (
    <span ref={tooltipRef}>
      <Tooltip open={open}>
        <TooltipTrigger asChild> 
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className={styles.triggerSpan}
            aria-label={`Show information about ${title}`}
            title={title}
          >
            <Icon iconName="InformationOutline" fill="currentColor" />
          </button>
        </TooltipTrigger>
        <TooltipContent className={styles.tooltipContent}>
          <strong>{title}</strong>
          <br />
          <p>{body}</p>
        </TooltipContent>
      </Tooltip>
    </span>
  );
};

// 2. Convert the React component to a web component, mapping attributes to props.
const KlvTooltip = r2wc(KlvTooltipWrapper, {
  props: {
    title: 'string',
    body: 'string',
  } as Record<keyof IKlvTooltipProps, 'string'>,
});

// 3. Export a function to register the custom element.
export const registerKlvTooltip = () => {
  if (windowIsDefined() && !customElements.get('klv-tooltip')) {
    customElements.define('klv-tooltip', KlvTooltip);
  }
};
